import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Table from "../ui/table/TableController";
import {
  companiesSelector,
  updateFloorState,
  updateFloor,
} from "../../redux/slices/companiesSlice";
import { uiSelector } from "../../redux/slices/uiSlice";
import { authSelector } from "../../redux/slices/authSlice";
import Loading from "./tableState/Loading";
import NoSearch from "./tableState/NoSearch";
import NoItems from "./tableState/NoItems";

const POS_SECTION = {
  background: "#eef4fd",
  color: "#346fcf",
  dividerColor: "#88aef5",
};

const PMS_SECTION = {
  background: "#f4effb",
  color: "#7b68b3",
  dividerColor: "#88aef5",
};

const ReveneuCentar = () => {
  const { floors, tableLoading, currentId } = useSelector(companiesSelector);
  const { searchQuery } = useSelector(uiSelector);
  const { token, integraInfo } = useSelector(authSelector);
  const dispatch = useDispatch();

  const [pmsOptions, setPmsOptions] = useState([]);
  const [pmsOptionsLoading, setPmsOptionsLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadPmsOptions = async () => {
      if (!token || !currentId) {
        if (isMounted) {
          setPmsOptions([]);
          setPmsOptionsLoading(false);
        }
        return;
      }

      setPmsOptionsLoading(true);

      try {
        const response = await fetch(
          "https://pmsapi.beesql.com/api/pms-web-app/company-floor-list",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              type: "revenue_center",
              x_token: token,
              company_id: currentId,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const result = await response.json();

        let list = [];

        if (Array.isArray(result)) {
          list = result;
        } else if (Array.isArray(result?.data)) {
          list = result.data;
        } else if (result && typeof result === "object") {
          list = [result];
        }

        const normalized = list.map((item) => ({
          value: item?.id ?? item?.floor_id ?? item?.pms_id ?? "",
          label: item?.name ?? item?.floor_name ?? item?.pms_name ?? "",
        }));

        if (isMounted) {
          setPmsOptions(normalized);
        }
      } catch (error) {
        console.error("Failed to load PMS dropdown options:", error);

        if (isMounted) {
          setPmsOptions([]);
        }
      } finally {
        if (isMounted) {
          setPmsOptionsLoading(false);
        }
      }
    };

    loadPmsOptions();

    return () => {
      isMounted = false;
    };
  }, [token, currentId]);

  const filteredFloors = useMemo(() => {
    const q = String(searchQuery || "").toLowerCase();

    return (floors || []).filter(
      (i) =>
        String(i.floor_id || "").toLowerCase().includes(q) ||
        String(i.floor_name || "").toLowerCase().includes(q) ||
        String(i.pms_id || "").toLowerCase().includes(q)
    );
  }, [floors, searchQuery]);

  const sectionHeaders = [
    {
      id: "pos",
      label: "POS",
      span: 3,
      background: POS_SECTION.background,
      color: POS_SECTION.color,
      dividerAfter: true,
      dividerColor: POS_SECTION.dividerColor,
    },
    {
      id: "pms",
      label: "PMS",
      span: 2,
      background: PMS_SECTION.background,
      color: PMS_SECTION.color,
    },
  ];

  const headerData = [
    {
      id: 1,
      label: "Company",
      width: "20%",
      sortable: false,
      sectionBackground: POS_SECTION.background,
    },
    {
      id: 2,
      label: "Id",
      width: "20%",
      sortable: true,
      sectionBackground: POS_SECTION.background,
    },
    {
      id: 3,
      label: "Name",
      width: "20%",
      sortable: true,
      sectionBackground: POS_SECTION.background,
      dividerAfter: true,
      dividerColor: POS_SECTION.dividerColor,
    },
    {
      id: 4,
      label: ["MEWSMICROS", "WRISTCOINMEWS"].includes(integraInfo) ? "ServiceID" : "PMS ID (RC)",
      width: "20%",
      sortable: true,
      sectionBackground: PMS_SECTION.background,
    },
    {
      id: 5,
      label: "Action",
      width: "20%",
      sortable: false,
      sectionBackground: PMS_SECTION.background,
    },
  ];

  const rowKeys = ["company_id", "floor_id", "floor_name", "pms_id"];

  const editable = ["pms_id"];

  const sortByData = [
    {},
    {
      label: "floor_id",
      type: "number",
    },
    {
      label: "floor_name",
      type: "string",
    },
    {
      label: "pms_id",
      type: "string",
    },
    {},
  ];

  const updateAction = (x) => {
    dispatch(updateFloorState(x));
    return dispatch(updateFloor(x));
  };

  const editableData = {
    pms_id: {
      type: "listBox",
      items: pmsOptions,
      inputType: "string",
      loading: pmsOptionsLoading,
    },
  };

  if (tableLoading) return <Loading />;

  if (filteredFloors.length === 0 && searchQuery) return <NoSearch />;

  if (filteredFloors.length === 0) return <NoItems />;

  return (
    <Table
      sortByData={sortByData}
      headerData={headerData}
      sectionHeaders={sectionHeaders}
      rowKeys={rowKeys}
      data={filteredFloors}
      editable={editable}
      editableData={editableData}
      row_key="floor_id"
      updateAction={updateAction}
    />
  );
};

export default ReveneuCentar;
